import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Alert,
  FormHelperText,
} from "@mui/joy";
import { useAuthStore } from "../../store/authStore";

interface UsernameSelectorProps {
  onSuccess: () => void;
}

export const UsernameSelector: React.FC<UsernameSelectorProps> = ({
  onSuccess,
}) => {
  const { checkUsernameAvailable, updateProfile, loading } = useAuthStore();
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<
    "idle" | "checking" | "available" | "taken"
  >("idle");

  // Check username availability
  const checkUsername = async (value: string) => {
    if (value.length < 3) {
      setUsernameStatus("idle");
      return;
    }

    setUsernameStatus("checking");
    try {
      const available = await checkUsernameAvailable(value);
      setUsernameStatus(available ? "available" : "taken");
    } catch {
      setUsernameStatus("idle");
    }
  };

  const handleUsernameChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setUsername(sanitized);

    // Reset status immediately when typing
    if (sanitized.length < 3) {
      setUsernameStatus("idle");
    }
  };

  // Check username on blur for better UX
  const handleUsernameBlur = () => {
    checkUsername(username);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (username.length < 3) {
      setError("Username must be at least 3 characters");
      return;
    }

    if (usernameStatus === "taken") {
      setError("Username is already taken");
      return;
    }

    if (usernameStatus !== "available") {
      // Check one more time before submitting
      const available = await checkUsernameAvailable(username);
      if (!available) {
        setError("Username is already taken");
        setUsernameStatus("taken");
        return;
      }
    }

    try {
      await updateProfile({ username });
      onSuccess();
    } catch (err) {
      setError((err as Error).message);
      setUsernameStatus("idle");
      // toast user that something went wrong
      alert("Failed to set username. Please try again.");
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ textAlign: "left" }}>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControl sx={{ mb: 3 }}>
        <FormLabel>Choose your username</FormLabel>
        <Input
          required
          placeholder="username"
          value={username}
          onChange={(e) => handleUsernameChange(e.target.value)}
          onBlur={handleUsernameBlur}
          size="lg"
        />
        {usernameStatus === "checking" && (
          <FormHelperText>Checking availability...</FormHelperText>
        )}
        {usernameStatus === "available" && (
          <FormHelperText sx={{ color: "success.500" }}>
            Username available
          </FormHelperText>
        )}
        {usernameStatus === "taken" && (
          <FormHelperText sx={{ color: "danger.500" }}>
            Username taken
          </FormHelperText>
        )}
        <FormHelperText>
          Only lowercase letters, numbers, and underscores allowed
        </FormHelperText>
      </FormControl>

      <Button
        type="submit"
        fullWidth
        size="lg"
        loading={loading}
        disabled={usernameStatus === "checking" || usernameStatus === "taken"}
        sx={{
          backgroundColor: "#FF6B4A",
          "&:hover": { backgroundColor: "#E55A3A" },
        }}
      >
        Continue
      </Button>
    </Box>
  );
};
